import { hasPermission } from '@icetus/contracts';
import type { Permission, Role } from '@icetus/contracts';

/**
 * 控制台侧栏的菜单结构。
 *
 * 抽成纯模块（不 import React）有两个理由：`App.tsx` 已经 1000 多行，
 * 再往里塞分组和权限判定只会更难读；而可见性判定是**有分支的业务逻辑**，
 * 放在组件里就只能靠手点五个角色去验，抽出来才能写测试。
 *
 * 分组口径是「客户要做什么」，不是「系统里有什么表」：
 * 客户不会想"我要去 categories 表"，他想的是"我要改网站长什么样"。
 * 所以「主题市场」和「页面内容」同组 —— 两者都在改前台的样子；
 * 「多语言翻译」也在这组 —— 它改的是另一种语言下的同一段文案。
 */

/** 分组键。顺序即侧栏渲染顺序 */
export type NavGroupKey = 'platform' | 'content' | 'commerce' | 'customers' | 'site' | 'account';

/**
 * 分组表。
 *
 * `label: null` 表示**不画组标题**。目前只有「平台总览」用它：
 * 它跨租户、且只有平台超管看得到，给它写个「平台」标题反而像
 * 上面还有一层没展开的东西。
 *
 * 「内容与外观」这个组名是权衡过的：组里的项一半是外观定义
 * （主题决定版式、页头页脚决定全站框架），单叫「内容」会让客户
 * 想换主题时去别处找。「站点信息」（公司名、品牌色、联系方式）
 * 也在这组 —— 它改的同样是"站看起来是什么样"，而不是"有哪些内容"。
 */
/*
 * 顺序按"客户每天从哪开始"排：**站点在最前**。
 *
 * 上一版把「内容与外观」放第一，理由是该组项最多、最先要编辑。但客户
 * 打开控制台的第一件事往往不是改页面，而是确认"我的站在哪、哪个域名"——
 * 站点、商品、外观、客户是逐层收窄的关系：先有站，才有商品要卖，
 * 才有页面要改，才有询盘要看。把站点压到第四组等于把入口藏在折叠里，
 * 客户要在五个组标题里先找到它。
 *
 * 「账号」永远垫底：它跟站上的事无关，是"关于我自己"的那一层。
 */
export const NAV_GROUPS: readonly { key: NavGroupKey; label: string | null }[] = [
  { key: 'platform', label: null },
  { key: 'site', label: '站点' },
  { key: 'commerce', label: '商品' },
  { key: 'content', label: '内容与外观' },
  { key: 'customers', label: '客户' },
  { key: 'account', label: '账号' },
];

/*
 * 表内顺序 = 组内渲染顺序（`navGroupsFor` 按 `NAV.filter` 保序取用）。
 * 所以调顺序就在这儿挪行，不要另建一张排序表 —— 两张表早晚会不一致。
 *
 * 2026-11-03 的两次调整都由客户提出来，且都指向同一件事：
 * **分组要按"我要找的那个东西"划，不是按系统里的表划**。
 *   · 媒体库、资料库从「商品」「客户」挪进「站点」—— 它们都是站上的素材；
 *   · 「内容与外观」重排为 站点信息 → 导航菜单 → 页面内容 → 页头页脚 → 多语言翻译。
 */
export const NAV = [
  {
    key: 'platform',
    group: 'platform',
    label: '平台总览',
    /* 这一页不给副标题：标题已经把话说完了，底下那张卡片又重复了一遍
       "所有租户的注册时间、站点与付费情况"。同一句话在屏幕上出现两次，
       读的人先是觉得啰嗦，接着开始怀疑自己是不是看错了两处不一样的东西。
       写成 null 而不是删掉 sub 字段：NAV 是 as const，删一个字段会让
       `navItem(x)?.sub` 在联合类型上报错，而不是安静地变成 undefined。 */
    sub: null,
  },
  // ── 站点：这个站是什么、挂在哪个地址、里面有哪些素材 ──────────────
  {
    key: 'sites',
    group: 'site',
    label: '我的站点',
    sub: '每个站点对应一套独立域名与内容库。',
    /* 这句只是给标题补一句“站点是什么”，顺着标题读得下去，
       独立成行会把页面头部撑得比别的页松一截。 */
    subInline: true,
  },
  {
    key: 'domains',
    group: 'site',
    /* 叫「域名绑定」而不是「域名」：侧栏里单写"域名"更像一个域名列表（资产），
       而这一页要做的是"把域名接上站"这件动作。客户找它时想的是
       "怎么让我的域名指向这里"，不是"我有几个域名"。 */
    label: '域名绑定',
    sub: '加两条 DNS 记录，平台自动签发证书。',
    /* 和「我的站点」同理：这句就是在解释标题里的"域名"是什么，
       跟在标题后面读得通，不必独占一行 */
    subInline: true,
  },
  /**
   * 媒体库跟着「站点」而不是「商品」（2026-11-03 挪过来的）。
   *
   * 它装的是**这个站上的图**：页头大图、案例照、证书扫描件、商品图。
   * 商品图只是其中一部分，而"我要换页头那张背景图"这件事跟商品没有关系。
   * 挂在「商品」组里时，客户为了一张页头图得先点进商品 —— 那儿只有
   * 商品与分类，找不到就会以为平台不支持传图。
   */
  {
    key: 'media',
    group: 'site',
    label: '媒体库',
    sub: null,
  },
  /**
   * 资料库（可下载的产品目录、认证证书）和媒体库是同一类东西，
   * 所以一起挪到「站点」（2026-11-03）。
   *
   * 上一版把它挂在「客户」组，理由是"买家留邮箱才拿得到 = 获客物料，
   * 和询盘是同一件事的两端"。这个理由在系统内部成立，在客户脑子里不成立：
   * 他要传一份新目录时不会想到去"客户"里找，那里只有询盘。
   * 现在「客户」组只剩「询盘」—— 那才是客户留下的东西；
   * 库里的文件是**我方的素材**，跟站上的图一样。
   */
  {
    key: 'resources',
    group: 'site',
    label: '资料库',
    sub: '产品目录、认证证书这些可下载的文件。买家留邮箱后才拿得到。',
    /* 副标题跟标题排在同一行：这一页的卡片里已经没有别的说明文字了，
       副标题再单独占一行，整页会比别的页松一截，而它本来就是一句
       可以顺着标题读下去的话。其余页面保持两行 —— 那些页的副标题是
       独立的一段解释，跟在 22px 的标题后面会把第一行挤得太长。 */
    subInline: true,
  },
  // ── 商品 ──────────────────────────────────────────────────────────
  {
    key: 'catalog',
    group: 'commerce',
    label: '商品与分类',
    /* 副标题删掉：「只有已上架的商品才出现在前台」这条规则在表头的状态筛选
       （草稿 / 已上架 / 已归档）和商品编辑页的状态字段上都已经写明，
       顶栏再讲一遍等于每个进这一页的人先读一句他已经知道的话。 */
    sub: null,
  },
  // ── 内容与外观：站长什么样 ────────────────────────────────────────
  /*
   * 组内顺序（2026-11-03 客户指定）：站点信息 → 导航菜单 → 页面内容 →
   * 页头页脚 → 多语言翻译。读下来是一条"从身份到细节"的线：
   * 这是哪个站（名字、Logo、品牌色）→ 站上有哪些入口 → 每页写什么 →
   * 全站共用的框架 → 另一种语言下的同一套东西。
   */
  {
    key: 'settings',
    group: 'content',
    /* 名字从「站点设置」改成「站点信息」：这一页填的是公司名、品牌色、
       联系方式这类**事实**，不是开关和偏好。"设置"听起来像一堆选项，
       客户会以为里面还有一个"高级"抽屉。 */
    label: '站点信息',
    /* 归在「内容与外观」而不是「站点」：它改的是"站看起来是什么样"，
       和主题、页头页脚是同一件事。留在站点组时，客户想换品牌色会先
       去外观那一组找，找不到再回头 —— 移到这儿之后，「站点」组只剩
       地址与素材，划分反而更干净。 */
    /* 无副标题：这一页从上到下就是几个带标题的卡片，写一句"这里有公司信息、
       品牌色、联系方式"只是把下面的目录抄了一遍，还多占一行 */
    sub: null,
  },
  {
    key: 'nav',
    group: 'content',
    label: '导航菜单',
    /* 从「页头页脚」里拆出来的独立一页。拆的理由不是它重，而是**两种意图
       混在一列**：页头页脚是"这一带由哪几块拼成、每块写什么"，菜单是
       "有哪些入口、指向哪"。客户想加一个产品分类时不会去"页头第 4 块"里找。
       无副标题：进页面就是站点下拉 + 菜单行 + 结构示意，标题已经把话说完了。 */
    sub: null,
  },
  {
    key: 'content',
    group: 'content',
    label: '页面内容',
    /* 这一页现在是**唯一的主编辑入口**：全屏里点哪儿改哪儿，页头和页脚也能
       直接点开 —— 所以副标题要回答"页头页脚在哪改"，否则客户还是去翻侧栏。
       原来这里写 null 的理由是"屏幕本身已经把在这里编辑说完了"；现在多了
       一条跨页面的事实（页头页脚影响全站），屏幕本身说不出来。 */
    sub: '点「全屏编辑」，页面上哪一段都能点开改 —— 页头和页脚也在里面。页头页脚属于全站，改动对所有页面生效。',
  },
  {
    key: 'shell',
    group: 'content',
    label: '页头页脚',
    /* 标成「高级」的入口。
       日常改页头页脚，现在在「页面内容」的全屏编辑里直接点它们就行 ——
       客户看到的页头就是他要改的页头，这才是所见即所得。
       这一页留下来的是全屏里做不到的事：按区域总览**全部**外壳块、
       重新生成、批量翻译。
       所以副标题不重复页面开头那句"全站生效"，而是回答"那我还来这儿干嘛"。 */
    sub: '高级：按区域总览全部外壳块，重新生成与翻译也在这里。日常直接点页面上的页头页脚就能改。',
  },
  {
    key: 'translation',
    group: 'content',
    label: '多语言翻译',
    sub: '从默认语言翻到其它语言：先写草稿，校对完再发布。按字符计费，每月有额度。',
  },
  {
    key: 'themes',
    group: 'content',
    label: '主题市场',
    /* 副标题删掉：进页面第一眼就是几张主题卡，"包含在套餐内 / 付费按月订阅"
       这种规则说明在卡片标题旁边已经写着，顶栏再说一遍是白占一行。 */
    sub: null,
    /* 侧栏不再单独给一个入口（见 `NAV_HIDDEN`）。页面本身还留着：
       订阅、续订、退订、平台超管发布主题都在那一页上。 */
  },
  // ── 客户：客户留下来的东西 ────────────────────────────────────────
  {
    key: 'leads',
    group: 'customers',
    label: '询盘',
    /* 副标题删掉：原来顶栏一句「站上收到的买家留言与资料索取…」，卡片标题底下
       又一句几乎一样的 —— 同一段话在一屏里出现两次。真正需要解释的
       「这一条是怎么来的」，在空态和「来源」列里都已经说了。 */
    sub: null,
  },
  // ── 账号：关于我自己 ──────────────────────────────────────────────
  {
    key: 'billing',
    group: 'account',
    label: '套餐与账单',
    sub: '账单每一行都可下钻到原始用量记录。',
  },
  {
    key: 'members',
    group: 'account',
    /* 从「成员」改成「成员管理」（2026-11-03）：侧栏里全是名词短语时，
       "成员"读起来像一个名单——点进去发现能邀请、改角色、移除，动作比预期多。
       补两个字把这一页是**管**人和权限说清楚，也跟「域名绑定」那种
       "动词在前"的命名对齐。 */
    label: '成员管理',
    /* 无副标题：原来那句"同事按角色分工：编辑能改内容…"把四种角色的能力
       压成一行，既说不全也记不住。现在页底有一张对照表，一句话能说清的事
       交给表格，不再占标题下面这块最贵的位置 */
    sub: null,
  },
  {
    key: 'audit',
    group: 'account',
    label: '操作记录',
    sub: '谁在什么时候改了什么。有争议的时候先看这里。',
  },
  /**
   * 个人资料在「账号」组的数据里，但**不按分组渲染**（见 `NAV_FOOTER`）：
   * 它是一个钉在侧栏底部、退出登录上方的按钮。
   *
   * 和同组三项都不同：**不跟租户走**。同一个人可能在三个客户站点里
   * 分别是不同的角色，但名字只有一个。页面分发时也因此要在"有没有租户"
   * 那个分支之前处理它。
   */
  {
    key: 'profile',
    group: 'account',
    label: '个人资料',
    // 副标题原本写着"你的名字、手机号与登录密码，所有站点通用"——
    // 卡头上的「所有站点通用」徽章已经说了同一件事，留一份就够
    sub: null,
  },
] as const;

export type NavItem = (typeof NAV)[number];
export type NavKey = NavItem['key'];

/**
 * 不进分组、单独做成底部按钮的项。
 *
 * 放「账号」组里时，客户要改密码得先展开账号、再在一行行里找 ——
 * 而它和套餐、成员、操作记录本来就不是一类事（那三项跟租户走，它不跟）。
 * 现在它和「退出登录」并排：两个都是"关于我这个账号"的动作。
 *
 * 用数组而不是在 NAV 项上加 `footer: true`：`NAV` 是 `as const` 的字面量
 * 联合，给其中一项加键会让类型推导要求其余项也补上它（和 `subInline`
 * 那处一样），而这里只想知道"哪些 key 在底栏"，一张清单就够。
 */
export const NAV_FOOTER: readonly NavKey[] = ['profile'];

/**
 * 有页面、但**不在侧栏里出现**的项。
 *
 * 目前只有「主题市场」。换主题的入口已经并进「站点信息 → 外观」：那一排
 * 卡片里有「当前主题 / 推荐 / 更多主题」，客户换皮这件事从头到尾在同一个
 * 页面里做完，侧栏里再单开一项只是让「内容与外观」更难扫。
 *
 * 页面**不能删**：订阅、续订、退订、以及平台超管发布主题都在那一页上。
 * 所以它是"藏起来"，不是"下线"。
 *
 * 用一张清单而不是给 NAV 项加 `hidden` 标记，理由同 `NAV_FOOTER`：
 * `NAV` 是 `as const` 的字面量联合，给其中一项加键会让其余项都被要求补上。
 */
export const NAV_HIDDEN: readonly NavKey[] = ['themes'];

/** 这一项是否只从别处进入（侧栏里不画） */
export function isHiddenNav(key: NavKey): boolean {
  return NAV_HIDDEN.includes(key);
}

/** 底栏项是否也是"导航项"。App 用它判"当前页是不是被裁掉了" */
export function isFooterNav(key: NavKey): boolean {
  return NAV_FOOTER.includes(key);
}

/**
 * 菜单项 → 权限点。
 *
 * 没列出来的项**不做权限过滤**，每一条都有理由，别随手补：
 * - `translation`：页内"发起翻译"按钮自己读 `translation:run`，
 *   光看不说的人（比如校对）也该进得去。
 * - `themes`：订阅是 owner 的事，但看市场、看已订阅状态谁都可以。
 * - `content/shell/catalog/media/leads/resources`：写操作由服务端判，
 *   一个编辑至少得能看见自己负责的那摊事。
 */
const NAV_PERMISSION: Partial<Record<NavKey, Permission>> = {
  domains: 'domain:manage',
  settings: 'site:update',
  billing: 'billing:manage',
  members: 'member:manage',
  audit: 'audit:read',
};

/**
 * 只读角色（viewer）能看见的项。
 *
 * `ROLE_PERMISSIONS.viewer` 是**空数组**，所以不能拿权限点去筛 ——
 * 机械地筛一遍，viewer 登录进来会是一个空侧栏。
 * 这里显式列清单：站上的东西（内容、商品、媒体）和客户来的东西（询盘、资料）
 * 都留着可看，钱、人、域名、设置这些看不见也不可惜。
 *
 * 这是一个**集合**，不表达顺序：渲染顺序由分组表决定。
 * 顺序写成和分组一致只是为了读起来顺，别把它当排序依据。
 */
export const VIEWER_NAV: readonly NavKey[] = [
  'sites',
  'media',
  'resources',
  'catalog',
  'content',
  'shell',
  'nav',
  'leads',
  'profile',
];

export interface NavContext {
  /**
   * 当前选中租户里的角色。没有租户时是 undefined ——
   * 两种情形：还没建站的新客户，以及拿运维令牌访问的超管。
   */
  role?: Role;
  /** 平台超管或运维令牌 */
  canPlatform: boolean;
  /**
   * 当前租户下有没有站点。
   *
   * **`undefined` 表示"还没问出来"**，此时按全量渲染。这个区分很重要：
   * 站点列表是异步拉的，如果一进页面就按"没站点"渲染，侧栏会先只剩一项、
   * 半秒后再全部出现 —— 看着像坏了。宁可先多显示几项。
   */
  hasSite?: boolean;
}

/**
 * 一个站点都没有时仍然能进的项。
 *
 * 只有「我的站点」。媒体库、资料库、商品、页面、域名都挂在具体站点上，
 * 零站点时点进去只能看到空列表，或者在后端撞 `tenant_required` 一类的错 ——
 * 与其让客户点进去发现什么都没有，不如先不给入口。
 *
 * 底栏（个人资料、退出）**不在这里判**：它由 `navFooterFor` 单独走，
 * 零站点时也必须留着，否则客户连改密码和登出都找不到地方。
 */
const NAV_WITHOUT_SITE: readonly string[] = ['sites'];

/**
 * 能不能看到某一项。
 *
 * 分支顺序有讲究：
 *   1. 平台组只看超管；
 *   2. **零站点时先收窄**，剩下的才按角色判 —— 否则新手客户会看到一屏
 *      点进去就空着的菜单；
 *   3. 没有角色时一律全放（拿运维令牌进来的那种情形）。
 */
export function isNavVisible(item: NavItem, ctx: NavContext): boolean {
  if (item.key === 'platform') return ctx.canPlatform;
  if (
    ctx.hasSite === false &&
    !isFooterNav(item.key) &&
    !NAV_WITHOUT_SITE.includes(item.key)
  ) {
    return false;
  }
  if (!ctx.role) return true;
  if (ctx.role === 'viewer') return VIEWER_NAV.includes(item.key);
  const need = NAV_PERMISSION[item.key];
  return !need || hasPermission(ctx.role, need);
}

export interface NavGroup {
  key: NavGroupKey;
  label: string | null;
  items: NavItem[];
}

/**
 * 按分组切好、已经过滤过的菜单。
 *
 * 空组直接丢掉：否则 viewer 会看到一个只有标题没有内容的「账号」……
 * 反过来说，只剩一项的组保留 —— 分组表达的是归属，不是数量。
 *
 * 底栏项（`NAV_FOOTER`）在这里排掉：它们在侧栏底部各有一个按钮，
 * 再在组里渲染一次就是同一个入口出现两遍。
 */
export function navGroupsFor(ctx: NavContext): NavGroup[] {
  const groups: NavGroup[] = [];
  for (const group of NAV_GROUPS) {
    const items = NAV.filter(
      (item) =>
        item.group === group.key &&
        !isFooterNav(item.key) &&
        !isHiddenNav(item.key) &&
        isNavVisible(item, ctx),
    );
    if (items.length > 0) groups.push({ key: group.key, label: group.label, items });
  }
  return groups;
}

/** 底部按钮那一排（目前只有个人资料），已按权限过滤 */
export function navFooterFor(ctx: NavContext): NavItem[] {
  return NAV.filter((item) => isFooterNav(item.key) && isNavVisible(item, ctx));
}

/**
 * 当前这一页在这个角色下还能不能停。
 *
 * 外壳用它判断"当前页被裁掉了要弹回我的站点"。三个分支都要在这儿，
 * 否则会漏：**侧栏里看不见 ≠ 不能停**。隐藏项（主题市场）压根不在
 * `navGroupsFor` 的结果里，照分组判断会把客户从主题市场一脚踢回站点页 ——
 * 看起来像点进去就被弹出来。
 */
export function navStaysFor(key: NavKey, ctx: NavContext): boolean {
  if (isHiddenNav(key)) return true;
  if (isFooterNav(key)) return navFooterFor(ctx).some((item) => item.key === key);
  return navGroupsFor(ctx).some((group) => group.items.some((item) => item.key === key));
}

/** 顶栏标题与副标题仍按 key 查，与分组无关 */
export function navItem(key: NavKey): NavItem | undefined {
  return NAV.find((item) => item.key === key);
}

/**
 * 这一页的副标题是否跟标题排在同一行。
 *
 * 只有显式写了 `subInline: true` 的项才排一行——没写就是两行，
 * 也就是说"两行"是默认、不需要每个条目都声明一遍。
 *
 * 用 `in` 收窄而不是 `item.subInline`：`NAV` 是 `as const` 的字面量联合，
 * 只有一张表里的那一项带这个键，直接点出来会让整张表的类型都要求补上它。
 * 判定逻辑放在这里而不是 App.tsx，理由和整张表放在这里一样：它是数据的一部分。
 */
export function navSubInline(key: NavKey): boolean {
  const item = NAV.find((i) => i.key === key);
  return item !== undefined && 'subInline' in item;
}

/**
 * 某一项属于哪个组。侧栏靠它决定"当前所在组要展开"。
 *
 * 底栏项返回 `null`：它不在任何一组里渲染。若照 `item.group` 返回 'account'，
 * 停在个人资料页时「账号」组会被自动展开、里面却没有一个高亮项 ——
 * 看起来像那个入口莫名消失了。
 */
export function navGroupKeyOf(key: NavKey): NavGroupKey | null {
  const item = NAV.find((i) => i.key === key);
  // NavKey 由 NAV 推导而来，理论上不会找不到。真找不到时也返回 null 而不是
  // 退回第一组：调用方拿到 null 只是不展开任何组，比展开一个无关的组更保守。
  if (!item || isFooterNav(key)) return null;
  return item.group;
}

/** 默认折叠状态：全部收起，由调用方在挂载后按当前页把所在组打开 */
export function collapsedByDefault(): ReadonlySet<NavGroupKey> {
  return new Set(NAV_GROUPS.map((group) => group.key));
}
